
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, Building, Eye, EyeOff } from 'lucide-react';
import { Company } from '@/lib/types';
import { getCompanyById, updateCompany } from '@/services/companyService';


const companySizes = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1001+ employees"
];

const EditCompany = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  
  const form = useForm<{
    name: string;
    industry: string;
    size: string;
    website: string;
    description: string;
    address: string;
    city: string;
    country: string;
    phone: string;
    currency: string;
    date_format: string;
    timezone: string;
    locale: string;
    outreach_instructions: string;
    default_from_email: string;
    default_from_name: string;
    default_reply_to_email: string;
    email_signature_html: string;
  }>({
    defaultValues: {
      name: '',
      industry: '',
      size: '',
      website: '',
      description: '',
      address: '',
      city: '',
      country: '',
      phone: '',
      currency: 'EUR',
      date_format: 'YYYY-MM-DD',
      timezone: '',
      locale: '',
      outreach_instructions: '',
      default_from_email: '',
      default_from_name: '',
      default_reply_to_email: '',
      email_signature_html: '',
    }
  });

  useEffect(() => {
    if (!isAdmin) {
      toast({
        title: "Access Denied",
        description: "Only administrators can edit companies.",
        variant: "destructive"
      });
      
      if (id) {
        navigate(`/companies/${id}`);
      } else {
        navigate('/companies');
      }
      return;
    }
    
    const fetchCompanyDetails = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const company = await getCompanyById(id);
        if (company) {
          form.reset({
            name: company.name || '',
            industry: company.industry || '',
            size: company.size || '',
            website: company.website || '',
            description: company.description || '',
            address: (company as any).address || '',
            city: (company as any).city || '',
            country: (company as any).country || '',
            phone: (company as any).phone || '',
            currency: company.currency || 'EUR',
            date_format: (company as any).date_format || 'YYYY-MM-DD',
            timezone: (company as any).timezone || '',
            locale: (company as any).locale || '',
            outreach_instructions: (company as any).outreach_instructions || '',
            default_from_email: (company as any).default_from_email || '',
            default_from_name: (company as any).default_from_name || '',
            default_reply_to_email: (company as any).default_reply_to_email || '',
            email_signature_html: (company as any).email_signature_html || '',
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Company not found"
          });
          navigate('/companies');
        }
      } catch (error) {
        console.error('Error fetching company details:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load company details"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCompanyDetails();
  }, [id, toast, navigate, form, isAdmin]);

  if (!isAdmin) {
    return null;
  }

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const onSubmit = async (data: any) => {
    if (!id) return;
    
    try {
      await updateCompany(id, data);
      toast({
        title: "Success",
        description: "Company details updated successfully"
      });
      navigate(`/companies/${id}`);
    } catch (error) {
      console.error('Error updating company:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update company details"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto p-4 md:p-6">
          <div className="flex items-center mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/companies')} 
              className="mr-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 container mx-auto p-4 md:p-6">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(`/companies/${id}`)} 
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Company
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold">Edit Company</h1>
        </div>

        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="mr-2 h-5 w-5" />
              Edit Company Details
            </CardTitle>
            <CardDescription>
              Update the information for this company
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter company name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry</FormLabel>
                      <FormControl>
                        <Input placeholder="E.g. Technology, Healthcare, Finance" {...field} />
                      </FormControl>
                      <FormDescription>
                        The industry or sector this company operates in
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="size"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Size</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        >
                          <option value="">Select company size</option>
                          {companySizes.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input placeholder="https://example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Brief description of the company"
                          className="min-h-[120px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium mb-4">Location</h3>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address</FormLabel>
                          <FormControl>
                            <Input placeholder="Street address" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. Dublin" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Country</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. Ireland" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. +353 1 234 5678" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium mb-4">Localisation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Currency</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. EUR, GBP, USD" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="date_format"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date Format</FormLabel>
                          <FormControl>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              {...field}
                            >
                              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                              <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="timezone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Timezone</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Europe/Dublin" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="locale"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Locale</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. en-IE, en-GB, en-US" {...field} />
                          </FormControl>
                          <FormDescription>
                            Used for spelling conventions in AI-generated content
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium mb-4">Default Email Sender</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configure the default sender details for outreach campaigns sent on behalf of this company.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="default_from_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>From Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Deadline Sales Team" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="default_from_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>From Email</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. info@deadline.ie" {...field} />
                          </FormControl>
                          <FormDescription>
                            Must be a verified sender in your SendGrid account
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="default_reply_to_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reply-To Email</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. sales@deadline.ie" {...field} />
                          </FormControl>
                          <FormDescription>
                            Optional — replies will go to this address
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium mb-4">Company Email Signature</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    This HTML signature will be appended to all outgoing emails from your company. You can include logos, links, and rich formatting.
                  </p>
                  <FormField
                    control={form.control}
                    name="email_signature_html"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Signature (HTML)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Paste your HTML email signature here..."
                            className="min-h-[120px] font-mono text-xs"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormDescription>
                          Rich text signature with images, links, and formatting. This appears at the bottom of all company emails.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.watch('email_signature_html') && (
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSignaturePreview(!showSignaturePreview)}
                        className="mb-2"
                      >
                        {showSignaturePreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                        {showSignaturePreview ? 'Hide Preview' : 'Show Preview'}
                      </Button>
                      {showSignaturePreview && (
                        <div className="border rounded-md p-4 bg-white">
                          <p className="text-xs text-muted-foreground mb-2 font-medium">Signature Preview:</p>
                          <div dangerouslySetInnerHTML={{ __html: form.watch('email_signature_html') }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium mb-4">AI Outreach Instructions</h3>
                  <FormField
                    control={form.control}
                    name="outreach_instructions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Custom Outreach Instructions</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="e.g. Always mention our HACCP certification. Focus on temperature-controlled logistics. Use a consultative tone..."
                            className="min-h-[150px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Custom instructions for AI-generated emails and call scripts. These will be included in every outreach generated for your company.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/companies/${id}`)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EditCompany;
